// Manhole Designer Types

// ============================================
// Phase 1: Hole Size Selection Constants & Utilities
// ============================================

/**
 * Boot types for pipe connections
 * - press-seal: Standard Press Seal Boot
 * - corn-seal: Trelle Borg (Corn Seal) Boot
 * - kor-n-seal: Kor-N-Seal Boot (alternative brand)
 */
export type BootType = 'press-seal' | 'corn-seal' | 'kor-n-seal' | 'none';

/**
 * Standard hole sizes available for selection (in inches)
 */
export const STANDARD_HOLE_SIZES: number[] = [
  6, 8, 10, 12, 14, 15, 16, 18, 20, 21, 22, 24, 26, 27, 28, 30, 32, 33, 36, 38, 39, 40, 42, 44, 48
];

/**
 * Joint height (bell overlap) by manhole diameter in inches.
 * Per Clay Prewitt's specifications from the manhole drawing review.
 * This is the height of the bell joint where sections connect.
 *
 * Used for:
 * - Stack height calculations (sections measured inside top of bell to top of spigot)
 * - Base section adjustments (must add joint height back)
 * - Riser section coring (add joint height to specified elevation)
 */
export const JOINT_HEIGHT_BY_DIAMETER: Record<number, number> = {
  48: 3,     // 48" diameter: 3" joint
  60: 5,     // 60" diameter: 5" joint
  72: 5.5,   // 72" diameter: 5.5" joint
  84: 5.5,   // 84" diameter: 5-6" (using 5.5" as estimate, TBD)
  96: 6,     // 96" diameter: TBD (estimate)
  120: 6,    // 120" diameter: TBD (estimate)
  144: 6,    // 144" diameter: TBD (estimate)
};

/**
 * Standard joint overlap for most calculations (3 inches)
 * Used as default when diameter-specific value not available
 */
export const DEFAULT_JOINT_HEIGHT_INCHES = 3;

/**
 * Get joint height for a given manhole diameter
 * @param diameterInches - The manhole inside diameter in inches
 * @returns Joint height in inches
 */
export function getJointHeightByDiameter(diameterInches: number): number {
  return JOINT_HEIGHT_BY_DIAMETER[diameterInches] || DEFAULT_JOINT_HEIGHT_INCHES;
}

/**
 * Hole size lookup table based on pipe diameter, connection type, and boot type
 * From Clay's specifications (Dec 2, 2025 meeting):
 * - 8" PVC with Press Seal Boot → 12" hole
 * - 8" PVC with Hole Only → 10" hole
 * - 36" PVC with Press Seal Boot → 38" hole
 * - 36" PVC with Corn Seal Boot → 39" hole
 *
 * General formula for boots: hole size ≈ pipe diameter + 4" (varies by manufacturer)
 */
export interface IHoleSizeLookupEntry {
  pipeDiameterInches: number;
  connectionType: 'hole' | 'boot' | 'adapter' | 'collar';
  bootType?: BootType;
  defaultHoleSizeInches: number;
}

export const HOLE_SIZE_LOOKUP_TABLE: IHoleSizeLookupEntry[] = [
  // Hole Only connections (pipe diameter + 2" clearance)
  { pipeDiameterInches: 4, connectionType: 'hole', defaultHoleSizeInches: 6 },
  { pipeDiameterInches: 6, connectionType: 'hole', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 8, connectionType: 'hole', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 10, connectionType: 'hole', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 12, connectionType: 'hole', defaultHoleSizeInches: 14 },
  { pipeDiameterInches: 15, connectionType: 'hole', defaultHoleSizeInches: 18 },
  { pipeDiameterInches: 18, connectionType: 'hole', defaultHoleSizeInches: 21 },
  { pipeDiameterInches: 21, connectionType: 'hole', defaultHoleSizeInches: 24 },
  { pipeDiameterInches: 24, connectionType: 'hole', defaultHoleSizeInches: 27 },
  { pipeDiameterInches: 27, connectionType: 'hole', defaultHoleSizeInches: 30 },
  { pipeDiameterInches: 30, connectionType: 'hole', defaultHoleSizeInches: 33 },
  { pipeDiameterInches: 36, connectionType: 'hole', defaultHoleSizeInches: 38 },

  // Press Seal Boot connections (pipe diameter + 4" for boot)
  { pipeDiameterInches: 4, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 6, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 8, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 10, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 12, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 15, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 20 },
  { pipeDiameterInches: 18, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 22 },
  { pipeDiameterInches: 21, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 26 },
  { pipeDiameterInches: 24, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 28 },
  { pipeDiameterInches: 27, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 32 },
  { pipeDiameterInches: 30, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 36 },
  { pipeDiameterInches: 36, connectionType: 'boot', bootType: 'press-seal', defaultHoleSizeInches: 38 },

  // Corn Seal (Trelle Borg) Boot connections (may require slightly larger holes)
  { pipeDiameterInches: 4, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 6, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 8, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 10, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 15 },
  { pipeDiameterInches: 12, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 15, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 20 },
  { pipeDiameterInches: 18, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 24 },
  { pipeDiameterInches: 21, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 27 },
  { pipeDiameterInches: 24, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 30 },
  { pipeDiameterInches: 27, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 33 },
  { pipeDiameterInches: 30, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 36 },
  { pipeDiameterInches: 36, connectionType: 'boot', bootType: 'corn-seal', defaultHoleSizeInches: 39 },

  // Kor-N-Seal Boot connections (similar to press seal)
  { pipeDiameterInches: 4, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 6, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 8, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 10, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 12, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 15, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 20 },
  { pipeDiameterInches: 18, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 22 },
  { pipeDiameterInches: 21, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 26 },
  { pipeDiameterInches: 24, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 28 },
  { pipeDiameterInches: 27, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 32 },
  { pipeDiameterInches: 30, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 36 },
  { pipeDiameterInches: 36, connectionType: 'boot', bootType: 'kor-n-seal', defaultHoleSizeInches: 38 },

  // Adapter connections (A-Lock)
  { pipeDiameterInches: 4, connectionType: 'adapter', defaultHoleSizeInches: 6 },
  { pipeDiameterInches: 6, connectionType: 'adapter', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 8, connectionType: 'adapter', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 10, connectionType: 'adapter', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 12, connectionType: 'adapter', defaultHoleSizeInches: 14 },
  { pipeDiameterInches: 15, connectionType: 'adapter', defaultHoleSizeInches: 18 },
  { pipeDiameterInches: 18, connectionType: 'adapter', defaultHoleSizeInches: 21 },
  { pipeDiameterInches: 21, connectionType: 'adapter', defaultHoleSizeInches: 24 },
  { pipeDiameterInches: 24, connectionType: 'adapter', defaultHoleSizeInches: 27 },

  // Sand Collar connections
  { pipeDiameterInches: 4, connectionType: 'collar', defaultHoleSizeInches: 8 },
  { pipeDiameterInches: 6, connectionType: 'collar', defaultHoleSizeInches: 10 },
  { pipeDiameterInches: 8, connectionType: 'collar', defaultHoleSizeInches: 12 },
  { pipeDiameterInches: 10, connectionType: 'collar', defaultHoleSizeInches: 14 },
  { pipeDiameterInches: 12, connectionType: 'collar', defaultHoleSizeInches: 16 },
  { pipeDiameterInches: 15, connectionType: 'collar', defaultHoleSizeInches: 20 },
  { pipeDiameterInches: 18, connectionType: 'collar', defaultHoleSizeInches: 24 },
  { pipeDiameterInches: 21, connectionType: 'collar', defaultHoleSizeInches: 27 },
  { pipeDiameterInches: 24, connectionType: 'collar', defaultHoleSizeInches: 30 },
];

/**
 * Get the default hole size based on pipe diameter, connection type, and boot type.
 * Used for smart defaults in the hole size dropdown.
 *
 * @param pipeDiameterInches - The pipe diameter in inches
 * @param connectionType - The connection type (hole, boot, adapter, collar)
 * @param bootType - Optional boot type for boot connections
 * @returns The default hole size in inches, or a calculated fallback
 */
export function getDefaultHoleSize(
  pipeDiameterInches: number,
  connectionType: 'hole' | 'boot' | 'adapter' | 'collar',
  bootType?: BootType
): number {
  // Find exact match in lookup table
  const match = HOLE_SIZE_LOOKUP_TABLE.find(entry =>
    entry.pipeDiameterInches === pipeDiameterInches &&
    entry.connectionType === connectionType &&
    (connectionType !== 'boot' || entry.bootType === bootType || (!bootType && entry.bootType === 'press-seal'))
  );

  if (match) {
    return match.defaultHoleSizeInches;
  }

  // If no exact match, find closest pipe size
  const entriesForConnectionType = HOLE_SIZE_LOOKUP_TABLE.filter(entry =>
    entry.connectionType === connectionType &&
    (connectionType !== 'boot' || entry.bootType === bootType || (!bootType && entry.bootType === 'press-seal'))
  );

  if (entriesForConnectionType.length > 0) {
    // Find closest pipe size
    const closest = entriesForConnectionType.reduce((prev, curr) =>
      Math.abs(curr.pipeDiameterInches - pipeDiameterInches) < Math.abs(prev.pipeDiameterInches - pipeDiameterInches)
        ? curr : prev
    );

    // Calculate proportional hole size
    const ratio = closest.defaultHoleSizeInches / closest.pipeDiameterInches;
    return Math.round(pipeDiameterInches * ratio);
  }

  // Fallback: use general formula
  if (connectionType === 'hole') {
    return pipeDiameterInches + 2; // 2" clearance for hole only
  } else if (connectionType === 'boot') {
    return pipeDiameterInches + 4; // 4" for boot
  } else {
    return pipeDiameterInches + 2; // Default 2" clearance
  }
}

/**
 * Get available hole sizes for a given pipe configuration.
 * Returns an array of standard sizes appropriate for the pipe diameter.
 *
 * @param pipeDiameterInches - The pipe diameter in inches
 * @param connectionType - The connection type
 * @returns Array of available hole sizes
 */
export function getAvailableHoleSizes(
  pipeDiameterInches: number,
  _connectionType: 'hole' | 'boot' | 'adapter' | 'collar'
): number[] {
  // Minimum hole size should be at least pipe diameter + 2"
  const minSize = pipeDiameterInches + 2;
  // Maximum practical hole size
  const maxSize = Math.min(pipeDiameterInches + 12, 48);

  return STANDARD_HOLE_SIZES.filter(size => size >= minSize && size <= maxSize);
}

// ============================================
// Phase 2: Invert-to-Rim Calculation Constants
// ============================================

/**
 * Joint overlap in inches - standard 3" overlap where sections connect
 */
export const JOINT_OVERLAP_INCHES = 3;

/**
 * Annular space requirements by boot type (in inches)
 * The gap between pipe and hole filled by boot or grout
 */
export const ANNULAR_SPACE_BY_BOOT_TYPE: Record<BootType, number> = {
  'press-seal': 2,
  'corn-seal': 1.5,
  'kor-n-seal': 2,
  'none': 0,
};

/**
 * Calculate annular space based on actual hole size vs pipe diameter.
 * This is more accurate than fixed boot-type values when hole size is known.
 *
 * Formula: Annular Space = (Hole Size - Pipe Diameter) / 2
 *
 * Examples:
 * - 8" pipe with 12" hole (standard boot): (12-8)/2 = 2"
 * - 8" pipe with 10" hole (grouting): (10-8)/2 = 1"
 *
 * @param holeSizeInches - The actual hole size in inches
 * @param pipeDiameterInches - The pipe diameter in inches
 * @returns The annular space in inches (one side only)
 */
export function calculateAnnularSpace(
  holeSizeInches: number | undefined,
  pipeDiameterInches: number
): number {
  if (!holeSizeInches || holeSizeInches <= pipeDiameterInches) {
    return 0;
  }
  return (holeSizeInches - pipeDiameterInches) / 2;
}

/**
 * Calculate the usable height (invert-to-rim) based on pipe configuration.
 *
 * Formula: Usable Height = Actual Height - (Hole Position + Annular Space)
 *
 * Core drilling measurement conventions:
 * - Bases: "6 inches up" = from very bottom of base
 * - Risers: "6 inches up" = from inside top of bell (add 3" for joint)
 *
 * @param actualHeightFeet - The actual component height in feet (includes joint)
 * @param holePositionInches - Position of hole from bottom in inches
 * @param bootType - Boot type for annular space calculation
 * @param isRiser - Whether this is a riser (affects measurement convention)
 * @returns The usable height (invert-to-rim) in feet
 */
export function calculateInvertToRim(
  actualHeightFeet: number,
  holePositionInches: number,
  bootType: BootType = 'press-seal',
  isRiser: boolean = false
): number {
  const annularSpaceInches = ANNULAR_SPACE_BY_BOOT_TYPE[bootType] || 2;

  // For risers, add 3" joint overlap to hole position
  const adjustedHolePosition = isRiser
    ? holePositionInches + JOINT_OVERLAP_INCHES
    : holePositionInches;

  // Convert offset to feet
  const offsetFeet = (adjustedHolePosition + annularSpaceInches) / 12;

  // Calculate usable height
  return Math.max(0, actualHeightFeet - offsetFeet);
}

/**
 * Calculate the core drill height (where to drill) from invert elevation.
 *
 * Core drilling measurement: The bottom of the hole, NOT the pipe invert.
 * Production crews need this measurement to know where to start drilling.
 *
 * Formula: Core Drill Height = Invert Position - Annular Space
 *
 * Example from Clay's drawing:
 * - 8" PVC pipe with 12" hole (kor-n-seal boot)
 * - Invert elevation: 8 inches up (where water flows)
 * - Annular space: (12 - 8) / 2 = 2 inches
 * - Core drill height: 8 - 2 = 6 inches up (where to start drilling)
 *
 * @param invertPositionInches - Invert elevation in inches from bottom
 * @param holeSizeInches - The hole diameter in inches
 * @param pipeDiameterInches - The pipe diameter in inches
 * @returns Core drill height in inches (bottom of hole position)
 */
export function calculateCoreDrillHeight(
  invertPositionInches: number,
  holeSizeInches: number | undefined,
  pipeDiameterInches: number
): number {
  const annularSpace = calculateAnnularSpace(holeSizeInches, pipeDiameterInches);
  return Math.max(0, invertPositionInches - annularSpace);
}

/**
 * Calculate the core drill height from invert elevation in feet.
 * Convenience wrapper that accepts feet and returns inches.
 *
 * @param invertElevationFeet - Invert elevation in feet
 * @param holeSizeInches - The hole diameter in inches
 * @param pipeDiameterInches - The pipe diameter in inches
 * @returns Core drill height in inches
 */
export function calculateCoreDrillHeightFromFeet(
  invertElevationFeet: number,
  holeSizeInches: number | undefined,
  pipeDiameterInches: number
): number {
  const invertPositionInches = invertElevationFeet * 12;
  return calculateCoreDrillHeight(invertPositionInches, holeSizeInches, pipeDiameterInches);
}

/**
 * Calculate the total outside height (for dig depth) including all joints
 *
 * @param components - Array of stack components
 * @returns Total outside height in feet
 */
export function calculateTotalOutsideHeight(
  components: Array<{ heightInches: number }>
): number {
  if (!components || components.length === 0) return 0;

  let totalInches = 0;
  components.forEach((comp, index) => {
    totalInches += comp.heightInches;
    // Add 3" joint for each connection (except the last component)
    if (index < components.length - 1) {
      totalInches += JOINT_OVERLAP_INCHES;
    }
  });

  return totalInches / 12;
}

/**
 * Extended structure code interface with calculation-related fields.
 * These fields may not be in the API schema yet but are needed for correct stacking.
 *
 * Per Clay Prewitt (2026-01-22):
 * - Floor thickness varies by structure diameter (48"=6", 60"/72"=8")
 * - Structure codes define default components and calculation parameters
 */
export interface IStructureCodeExtended {
  id: string;
  structureCode: string;
  name: string;
  description?: string;
  plantId: string;
  isActive: boolean;

  // Existing fields
  defaultCoreDrillHeightInches?: number;

  // NEW: Structure dimension fields (for stacking calculations)
  diameterInches?: number;           // 48, 60, 72, 96
  floorThicknessInches?: number;     // 6" for 48", 8" for 60"/72"
  wallThicknessInches?: number;      // Typically 5"
  insideCircumferenceFeet?: number;  // π × diameter (for steps calculation)

  // NEW: Default components (auto-populate when structure code selected)
  defaultCastingId?: string;
  defaultConeId?: string;
  defaultRingSizeInches?: number;    // Usually 6"
  includePreChannel?: boolean;       // Auto-add for sewer manholes

  // NEW: Structure classification
  structureType?: 'Sanitary' | 'Storm' | 'Water' | 'Custom';

  createdAt?: string;
  updatedAt?: string;
}

/**
 * Get floor thickness from a structure code, with fallback to defaults.
 */
export function getStructureCodeFloorThickness(
  structureCode: IStructureCodeExtended | undefined
): number {
  if (structureCode?.floorThicknessInches !== undefined) {
    return structureCode.floorThicknessInches;
  }
  // Fall back to diameter-based default
  const diameter = structureCode?.diameterInches || 48;
  return getDefaultFloorThickness(diameter);
}

export interface IManholeProject {
  id?: string;
  projectId?: string;
  userId?: string;
  accountId: string | number;  // Required: Links to customer account
  jobId?: string | number;  // Optional: Only set after project is awarded
  customerName?: string;  // Denormalized from account
  customerContact?: string;  // Denormalized from account
  jobName?: string;  // Denormalized from job (when awarded)
  jobNumber?: string;  // Denormalized from job (when awarded)
  jobIdentifier?: string;  // NTX format: YY-XXXXX (auto-generated)
  structureId: string;
  station: string;
  structureType?: 'Sanitary' | 'Storm' | 'Water' | 'Custom';  // Backend uses structureType - MOVED to structure level
  type?: 'Sanitary' | 'Storm' | 'Water' | 'Custom';  // Keep for compatibility - MOVED to structure level
  structureSubType?: 'Type 1' | 'Type 2' | 'Type 3' | 'Custom';  // NEW: Determines catch depth
  catchDepthFeet?: number;  // NEW: Automatic from subtype or manual
  city?: string;  // From account or job
  state?: string;  // From account or job
  salesperson?: string;  // From job when awarded
  salespersonId?: string;  // User ID reference (for dropdown after Jan 1)
  estimator?: string;  // Estimator name (text until Jan 1)
  estimatorId?: string;  // User ID reference (for dropdown after Jan 1)
  phase?: string;  // NEW: Project phase (Phase 1, Phase 2, etc.)
  revision?: string;
  rimElevation?: number;  // Backend stores at root level
  lowestInvert?: number;  // Backend calculated field
  requiredHeight?: number;  // Backend calculated field
  manholeStructure?: IManholeStructure;  // May not always be present
  castingId?: string;  // NEW: Selected casting type
  castingType?: string;  // NEW: Casting description
  formAssignment?: string;  // NEW: Production form assignment
  hatchType?: string;  // NEW: Required hatch specification
  hatchPONumber?: string;  // NEW: PO for special order hatches
  calculationData?: string;  // JSON string from backend
  drawingData?: string;  // JSON string from backend
  status?: 'draft' | 'estimate' | 'quoted' | 'awarded' | 'revised' | 'completed';
  quoteNumber?: string;  // Link to quote/estimate
  paymentTerms?: string;  // Payment terms (auto-filled from customer or COD)
  awardedDate?: Date;  // When estimate became a job
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Default floor thickness by manhole diameter (in inches)
 * Per Clay Prewitt (confirmed 2026-01-29): 48" = 6", 54"-72" = 8", 84"+ = 12"
 */
export const DEFAULT_FLOOR_THICKNESS: Record<number, number> = {
  48: 6,    // 48" diameter = 6" floor
  54: 8,    // 54" diameter = 8" floor
  60: 8,    // 60" diameter = 8" floor
  70: 8,    // 70" diameter = 8" floor
  72: 8,    // 72" diameter = 8" floor
  84: 12,   // 84" diameter = 12" floor
  96: 12,   // 96" diameter = 12" floor
  120: 12,  // 120" diameter = 12" floor
  144: 12,  // 144" diameter = 12" floor
};

/**
 * Get default floor thickness for a given diameter.
 * Falls back by range: >=84" → 12", >=54" → 8", else → 6"
 */
export function getDefaultFloorThickness(diameterInches: number): number {
  if (DEFAULT_FLOOR_THICKNESS[diameterInches] !== undefined) {
    return DEFAULT_FLOOR_THICKNESS[diameterInches];
  }
  if (diameterInches >= 84) return 12;
  if (diameterInches >= 54) return 8;
  return 6;
}

/**
 * Get the invert-to-first-joint height for a base.
 * This is the critical value for stacking calculations.
 *
 * Per Clay Prewitt / NTX PDF:
 *   invertToFirstJoint = (nominalHeightInches + jointHeight - floorThickness - annularSpace) / 12
 *
 * Example for 48" x 3' base:
 *   (36 + 3 - 6 - 2) / 12 = 2.583 ft
 *
 * The joint bell height is ADDED because the actual base is taller than nominal
 * (e.g., a 36" nominal base is really 39" with the 3" bell).
 * Floor thickness and annular space are subtracted because they sit below the pipe invert.
 *
 * @param diameterInches - Base diameter in inches
 * @param nominalHeightFeet - Base nominal height in feet (2, 3, or 4)
 * @returns Height from invert to first joint in feet
 */
export function getBaseInvertToFirstJoint(
  diameterInches: number,
  nominalHeightFeet: number,
  annularSpaceInches: number = 2
): number {
  const nominalHeightInches = nominalHeightFeet * 12;
  const jointHeight = getJointHeightByDiameter(diameterInches);
  const floorThickness = getDefaultFloorThickness(diameterInches);

  return (nominalHeightInches + jointHeight - floorThickness - annularSpaceInches) / 12;
}

/**
 * Get the effective height of a stack component including any height modifier.
 * Per Dec 19 meeting (Justin's request): allow +/- height adjustment
 * on individual stack components for non-standard sizes.
 */
export function getEffectiveHeightInches(component: IStackComponent): number {
  return (component.product?.heightInches || 0) + (component.heightModifierInches || 0);
}

/**
 * Calculate invert-to-first-joint from base properties.
 * Use this when the base product has detailed dimensions.
 *
 * @param baseHeightInches - Total base height in inches
 * @param floorThicknessInches - Floor thickness in inches
 * @param annularSpaceInches - Annular space for pipe in inches
 * @returns Height from invert to first joint in feet
 */
export function calculateInvertToFirstJoint(
  baseHeightInches: number,
  floorThicknessInches: number,
  annularSpaceInches: number
): number {
  // Height from bottom of base to invert
  const belowInvert = floorThicknessInches + annularSpaceInches;
  // Height from invert to top of base (first joint)
  const invertToJoint = baseHeightInches - belowInvert;
  return invertToJoint / 12;
}

/**
 * Calculate overall depth (contractor dig depth).
 *
 * Per Clay Prewitt (2026-01-22):
 * "How far is it to the bottom of the manhole? You're going to add .67 to it
 * in order to get to the bottom... you have 2 of annular space... and a 6 inch floor.
 * So you've got 8 inches before you're to the actual bottom."
 *
 * Formula: Overall Depth = Rim-to-Invert + Annular Space + Floor Thickness
 *
 * @param rimToInvertFeet - Distance from rim to pipe invert
 * @param floorThicknessInches - Floor thickness in inches
 * @param annularSpaceInches - Annular space in inches
 * @returns Overall depth in feet (for dig calculations)
 */
export function calculateOverallDepth(
  rimToInvertFeet: number,
  floorThicknessInches: number,
  annularSpaceInches: number
): number {
  const belowInvertFeet = (floorThicknessInches + annularSpaceInches) / 12;
  return rimToInvertFeet + belowInvertFeet;
}

/**
 * Calculate the height below invert (floor + annular space).
 *
 * @param floorThicknessInches - Floor thickness in inches
 * @param annularSpaceInches - Annular space in inches
 * @returns Height below invert in feet
 */
export function calculateBelowInvertHeight(
  floorThicknessInches: number,
  annularSpaceInches: number
): number {
  return (floorThicknessInches + annularSpaceInches) / 12;
}

/**
 * Calculate sump effect on remaining height.
 *
 * Per Clay Prewitt (2026-01-22):
 * When remaining goes negative (structure too tall), the user enters a sump value.
 * The sump pushes the base down, BUT it doesn't offset 1:1 because the annular
 * space must also be accounted for.
 *
 * @param negativeRemainingFeet - The negative remaining (how much too tall, as positive number)
 * @param sumpDepthFeet - User-entered sump depth
 * @param annularSpaceInches - Annular space for the outlet pipe
 * @returns Object with effective remaining and core drill shift
 */
export function calculateSumpEffect(
  negativeRemainingFeet: number,
  sumpDepthFeet: number,
  annularSpaceInches: number
): { effectiveRemainingFeet: number; coreDrillShiftInches: number } {
  // The sump pushes the base down, but the annular space isn't fully absorbed
  // Per Clay: "you have to add that two inches back into your 0.42"
  const annularOffsetFeet = annularSpaceInches / 12;
  const effectiveRemainingFeet = -negativeRemainingFeet + sumpDepthFeet - annularOffsetFeet;
  const coreDrillShiftInches = sumpDepthFeet * 12;

  return {
    effectiveRemainingFeet: Math.max(0, effectiveRemainingFeet),
    coreDrillShiftInches,
  };
}

/**
 * Calculate core drill height from invert elevation (for inlet pipes)
 * Per Justin Peters: Auto-calculate based on invert elevation delta from lowest invert
 *
 * @param invertElevation - Invert elevation in feet
 * @param lowestInvert - Lowest invert elevation in feet (outlet pipe)
 * @param pipeDirection - Whether this is inlet or outlet
 * @param structureCodeDefaultHeight - Default height from structure code (for outlet)
 * @returns Core drill height in inches
 */
export function calculateCoreDrillHeightFromInvert(
  invertElevation: number,
  lowestInvert: number,
  pipeDirection: 'inlet' | 'outlet',
  structureCodeDefaultHeight?: number
): number {
  // For outlet pipe, use structure code default
  if (pipeDirection === 'outlet' && structureCodeDefaultHeight !== undefined) {
    return structureCodeDefaultHeight;
  }

  // For inlet pipes, calculate from elevation delta relative to lowest invert
  // The delta in feet, converted to inches
  const heightAboveLowestInvert = (invertElevation - lowestInvert) * 12;

  // Add minimum base offset (typically 6" from floor)
  const baseOffset = 6;

  return Math.max(baseOffset, Math.round(heightAboveLowestInvert + baseOffset));
}

/**
 * Increment revision letter (A -> B -> C ... Z -> AA)
 */
export function incrementRevision(currentRevision?: string): string {
  if (!currentRevision) return 'A';

  // Handle single letter
  if (currentRevision.length === 1) {
    const code = currentRevision.charCodeAt(0);
    if (code >= 90) return 'AA'; // Z -> AA
    return String.fromCharCode(code + 1);
  }

  // Handle multi-letter (AA, AB, etc.)
  const lastChar = currentRevision.charCodeAt(currentRevision.length - 1);
  if (lastChar >= 90) {
    // Last char is Z, increment previous
    return incrementRevision(currentRevision.slice(0, -1)) + 'A';
  }
  return currentRevision.slice(0, -1) + String.fromCharCode(lastChar + 1);
}

/**
 * Generate job identifier in YY-XXXXX format
 * @param existingIdentifiers - Array of existing job identifiers to check against
 * @returns Next available job identifier (e.g., "25-00001")
 */
export function generateJobIdentifier(existingIdentifiers: string[]): string {
  const year = new Date().getFullYear().toString().slice(-2); // "25" for 2025
  const yearPrefix = `${year}-`;

  // Find highest existing number for current year
  const existingNumbers = existingIdentifiers
    .filter(id => id?.startsWith(yearPrefix))
    .map(id => parseInt(id.slice(3), 10))
    .filter(n => !isNaN(n));

  const nextNumber = existingNumbers.length > 0
    ? Math.max(...existingNumbers) + 1
    : 1;

  return `${year}-${nextNumber.toString().padStart(5, '0')}`;
}

export interface IManholeStructure {
  rimElevation: number;
  pipes: IPipeConnection[];
  internals?: IManholeInternals;
  components?: IStackComponent[];
  // NEW: Structure-level fields (moved from project level per Justin's feedback)
  structureType?: 'Sanitary' | 'Storm' | 'Water' | 'Custom';
  // NEW: Contractor advisory notes
  notes?: string;
  // NEW: Revision tracking (auto-increments on save)
  revision?: string;
  lastModified?: string;
  modifiedBy?: string;
  // NEW: Floor thickness (varies by diameter, editable for heavy-duty)
  floorThicknessInches?: number;
  // NEW: Coating options
  interiorCoating?: {
    type: 'none' | 'epoxy' | 'polyurea' | 'cement-mortar' | 'custom';
    customDescription?: string;
  };
  exteriorCoating?: {
    type: 'none' | 'bituminous' | 'epoxy' | 'custom';
    customDescription?: string;
  };
  // NEW: Concrete additives (e.g., sidepack)
  additives?: string[];
  // NEW: Flag for structures with warnings needing review
  hasWarnings?: boolean;

  // NEW: Sump for shallow manholes
  // Per Clay Prewitt (2026-01-22): When remaining goes negative, user enters sump
  // depth to push the base down, creating extra depth below the pipe.
  sumpDepthFeet?: number;

  // NEW: Structure dimensions (populated from structure code defaults)
  diameterInches?: number;
  wallThicknessInches?: number;
  insideCircumferenceFeet?: number;
}

export interface IManholeInternals {
  hasSteps: boolean;
  hasLadder: boolean;
  stepAngleDegrees?: number;  // 0-360 degrees from north
  stepType?: 'standard' | 'wide' | 'safety';
  ladderType?: string;  // Extended: 'none' | 'steel' | 'polypropylene' | 'polypropylene-hanging' | 'fixed' | 'removable'
  hasBench?: boolean;  // Flow channel bench
  hasDropBowl?: boolean;  // Drop bowl connection
  dropBowlSizeInches?: number;  // Drop bowl inlet size
  // Phase 5: Extended internal features
  hasPreChannelInvert?: boolean;  // Pre-formed channel invert
  hasGULiner?: boolean;  // Corrosion protection liner
  hasCustomInternal?: boolean;  // Custom internal component
  customInternalDescription?: string;  // Description for custom internal
}

export interface IPipeConnection {
  id?: string;
  manholeProjectId?: string;  // Foreign key for separate service
  pipeNumber: number;
  invertElevation: number;
  pipeDiameterInches: number;
  pipeMaterial: string;
  angleDegrees: number;        // 0-360 degrees from outlet (0° = Outlet/South)
  direction?: 'inlet' | 'outlet'; // Flow direction
  connectionType?: 'hole' | 'boot' | 'adapter' | 'collar'; // Connection method
  bootType?: BootType;  // Phase 1: Boot manufacturer/type for hole size calculation
  holeType?: string;
  holeSizeInches?: number;
  holeMethod?: 'cast-in' | 'cored' | 'field-cut';  // How the hole is created
  holePositionInches?: number;  // Phase 2: Position of hole from bottom of component
  componentLocation?: 'base' | 'riser' | 'cone';  // Phase 2: Which component the hole is in
  requiresSpecialFormer?: boolean;  // Needs special hole former
  // Phase 2: Calculated invert-to-rim for this pipe
  invertToRimFeet?: number;
  // Core drill height in inches (defaults from structure code, editable per pipe)
  coreDrillHeightInches?: number;
  // NEW: Flag to prevent auto-calculation of core drill height
  manualCoreDrillOverride?: boolean;
  // NEW: Pipe system type (per structure can have mixed types)
  pipeSystem?: 'Sanitary' | 'Storm' | 'Water';
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Pipe preset configuration for quick pipe selection.
 * Allows users to define common pipe configurations for reuse.
 */
export interface IPipePreset {
  id: string;
  name: string;                    // e.g., "8\" PVC with Press Seal"
  pipeDiameterInches: number;
  pipeMaterial: string;
  connectionType: 'hole' | 'boot' | 'adapter' | 'collar';
  bootType?: BootType;
  defaultHoleSizeInches: number;
  defaultHolePositionInches: number;
  pipeSystem?: 'Sanitary' | 'Storm' | 'Water';
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Default pipe presets for common configurations.
 */
export const DEFAULT_PIPE_PRESETS: IPipePreset[] = [
  {
    id: 'preset-8-pvc-press',
    name: '8" PVC with Press Seal',
    pipeDiameterInches: 8,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 12,
    defaultHolePositionInches: 6,
    pipeSystem: 'Sanitary',
    isActive: true,
  },
  {
    id: 'preset-8-pvc-hole',
    name: '8" PVC Hole Only',
    pipeDiameterInches: 8,
    pipeMaterial: 'PVC 3034',
    connectionType: 'hole',
    defaultHoleSizeInches: 10,
    defaultHolePositionInches: 6,
    pipeSystem: 'Sanitary',
    isActive: true,
  },
  {
    id: 'preset-6-pvc-press',
    name: '6" PVC with Press Seal',
    pipeDiameterInches: 6,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 10,
    defaultHolePositionInches: 6,
    pipeSystem: 'Sanitary',
    isActive: true,
  },
  {
    id: 'preset-10-pvc-press',
    name: '10" PVC with Press Seal',
    pipeDiameterInches: 10,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 16,
    defaultHolePositionInches: 6,
    pipeSystem: 'Sanitary',
    isActive: true,
  },
  {
    id: 'preset-12-pvc-press',
    name: '12" PVC with Press Seal',
    pipeDiameterInches: 12,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 16,
    defaultHolePositionInches: 6,
    pipeSystem: 'Sanitary',
    isActive: true,
  },
  {
    id: 'preset-15-pvc-press',
    name: '15" PVC with Press Seal',
    pipeDiameterInches: 15,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 20,
    defaultHolePositionInches: 8,
    pipeSystem: 'Storm',
    isActive: true,
  },
  {
    id: 'preset-18-pvc-press',
    name: '18" PVC with Press Seal',
    pipeDiameterInches: 18,
    pipeMaterial: 'PVC 3034',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 22,
    defaultHolePositionInches: 10,
    pipeSystem: 'Storm',
    isActive: true,
  },
  {
    id: 'preset-8-rcp-press',
    name: '8" RCP with Press Seal',
    pipeDiameterInches: 8,
    pipeMaterial: 'RCP',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 12,
    defaultHolePositionInches: 6,
    pipeSystem: 'Storm',
    isActive: true,
  },
  {
    id: 'preset-12-rcp-press',
    name: '12" RCP with Press Seal',
    pipeDiameterInches: 12,
    pipeMaterial: 'RCP',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 16,
    defaultHolePositionInches: 8,
    pipeSystem: 'Storm',
    isActive: true,
  },
  {
    id: 'preset-15-rcp-press',
    name: '15" RCP with Press Seal',
    pipeDiameterInches: 15,
    pipeMaterial: 'RCP',
    connectionType: 'boot',
    bootType: 'press-seal',
    defaultHoleSizeInches: 20,
    defaultHolePositionInches: 10,
    pipeSystem: 'Storm',
    isActive: true,
  },
];

/**
 * Catalog product interface for manhole components.
 *
 * MIGRATION NOTE (Dec 2025): The manhole-components service schema changed:
 * - ID format: UUID → 'mh_xxxxxxxx'
 * - componentType: lowercase → UPPERCASE (BASE, RISER, SECTION, CONE, FLAT_TOP, ADJUST_RING, OTHER)
 * - productCode removed, use componentCode
 * - cost → estimatedCost (with new cost breakdown fields)
 */
export interface ICatalogProduct {
  id: string | number;  // Format: 'mh_xxxxxxxx' (new) or UUID (legacy)
  productId?: string;  // May have both productId and componentCode
  componentCode: string;  // Primary identifier (replaces productCode)
  name?: string;  // Backend includes name field
  description: string;
  // Component type - now UPPERCASE: BASE, RISER, SECTION, CONE, FLAT_TOP, ADJUST_RING, OTHER
  type?: 'BASE' | 'RISER' | 'SECTION' | 'CONE' | 'FLAT_TOP' | 'ADJUST_RING' | 'OTHER' |
         // Legacy lowercase values for backwards compatibility during migration
         'base' | 'riser' | 'cone' | 'slab' | 'ring' | 'frame' | 'cover' | 'accessory';
  manholeType?: string;  // Backend field (deprecated, use type)
  heightInches: number;
  diameterInches: number;
  wallThicknessInches?: number;
  weightLbs?: number;  // Frontend field name
  weight?: number;  // Backend uses this

  // Stack calculation fields (for BASE components)
  // Per Clay Prewitt (2026-01-22): This is the CRITICAL value for stacking
  invertToFirstJointFeet?: number;  // Height from pipe invert to first joint (for bases)
  nominalHeightFeet?: number;       // Nominal height in feet (2, 3, or 4 for bases)

  // Cost fields (new schema)
  estimatedCost?: number;  // Total estimated cost (replaces cost/costUSD)
  totalLaborCost?: number;  // Labor cost component
  totalOverheadCost?: number;  // Overhead cost component
  standardCostPerYard?: number;  // Cost per yard of concrete

  // Legacy cost fields (for backwards compatibility)
  costUSD?: number;  // Deprecated: use estimatedCost
  basePrice?: number;  // Deprecated: use estimatedCost

  // Manufacturing fields (new schema)
  formId?: string;  // Manufacturing form reference
  departmentId?: string;  // Department reference
  yardsPerUnit?: number;  // Concrete yards per unit
  cubicMetersPerUnit?: number;  // Cubic meters per unit
  inboundFreightPercent?: number;  // Freight percentage

  // Metadata
  isManholeProduct?: boolean;  // Backend filter field
  isActive?: boolean;  // Active/inactive flag (new)
  active?: boolean;  // Legacy: use isActive
}

export interface IStackComponent {
  instanceId: string;
  product: ICatalogProduct;
  positionInStack?: number;  // Frontend uses this
  positionInManhole?: number;  // Backend might use this
  bottomElevation: number;
  // NEW: Height modifier for non-standard section heights (e.g., 3'7" instead of 4')
  // Positive or negative value in inches to adjust from standard height
  heightModifierInches?: number;
}

export interface IBillOfMaterialsEntry {
  item: string;
  description: string;
  quantity: number;
  weight: number;
  unitCost: number;
  totalCost: number;
}

export interface IManholeQuote {
  id?: string;
  accountId: string | number;
  manholeProjectId?: string;
  quoteNumber: string;
  items: IBillOfMaterialsEntry[];
  subtotal: number;
  tax?: number;
  total: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  validUntil?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

// ============================================
// Phase 6: Purchased Item Tracking Types
// ============================================

/**
 * Item source tracking - distinguishes manufactured vs purchased items
 */
export type ItemSourceType = 'manufactured' | 'purchased' | 'subcontracted';

/**
 * Purchased item categories for GU liner, hatches, etc.
 */
export type PurchasedItemCategory =
  | 'gu-liner'        // GU Liner material
  | 'hatch'           // Access hatches
  | 'boot'            // Pipe boots (Press Seal, Corn Seal)
  | 'casting'         // Frame and cover castings
  | 'steps'           // Pre-cast steps
  | 'ladder'          // Access ladders
  | 'safety-grate'    // Safety grates
  | 'coating'         // Special coatings
  | 'other';          // Other purchased items

/**
 * Date tracking status for purchased items
 */
export type DateTrackingStatus = 'pending' | 'scheduled' | 'received' | 'installed';

/**
 * Purchased Item Tracking interface
 * Tracks PO dates, anticipation, and actual receipt for purchased items
 */
export interface IPurchasedItemTracking {
  id?: string;
  structureId: string;          // Link to manhole structure
  projectId?: string;           // Link to project
  itemCategory: PurchasedItemCategory;
  itemDescription: string;      // Description of the purchased item
  supplierName?: string;        // Vendor/supplier name
  poNumber?: string;            // Purchase order number

  // Phase 6: Date tracking fields
  anticipationDate?: string;    // When item is expected to arrive
  scheduledDate?: string;       // When production is planned for this structure
  orderedDate?: string;         // When PO was placed
  receivedDate?: string;        // When item was actually received
  installedDate?: string;       // When item was installed (if applicable)
  deliveredDate?: string;       // When shipped to customer

  // Alert tracking
  hasDateMismatch?: boolean;    // True if scheduled date differs from anticipation
  dateMismatchDays?: number;    // Number of days difference
  alertSent?: boolean;          // Whether mismatch alert was sent
  alertSentDate?: string;       // When alert was sent

  // Status tracking
  status: DateTrackingStatus;
  notes?: string;

  createdAt?: string;
  updatedAt?: string;
}

/**
 * Structure production schedule with all date fields
 */
export interface IStructureProductionSchedule {
  structureId: string;
  priority?: string;            // Production priority number
  customerNeededDate?: string;  // Date customer needs the structure

  // Phase 6: Additional production dates
  scheduledProductionDate?: string;   // When scheduled for production
  actualProductionDate?: string;      // When actually produced
  qualityCheckDate?: string;          // When QC completed
  readyForDeliveryDate?: string;      // When ready for shipment
  actualDeliveryDate?: string;        // When delivered

  // Purchased items for this structure
  purchasedItems?: IPurchasedItemTracking[];

  // Status flags
  hasPurchasedItems: boolean;
  allItemsReceived: boolean;
  hasDateConflicts: boolean;
}

/**
 * Check if there's a date mismatch between anticipation and scheduled dates
 * @param anticipationDate - Expected arrival date
 * @param scheduledDate - Scheduled production date
 * @param thresholdDays - Days difference to trigger alert (default 3)
 * @returns Object with mismatch status and days difference
 */
export function checkDateMismatch(
  anticipationDate?: string,
  scheduledDate?: string,
  thresholdDays: number = 3
): { hasMismatch: boolean; daysDifference: number; alertRequired: boolean } {
  if (!anticipationDate || !scheduledDate) {
    return { hasMismatch: false, daysDifference: 0, alertRequired: false };
  }

  const anticipation = new Date(anticipationDate);
  const scheduled = new Date(scheduledDate);
  const diffTime = scheduled.getTime() - anticipation.getTime();
  const daysDifference = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  // Alert if production is scheduled before item is expected
  // Or if there's more than threshold days difference
  const hasMismatch = daysDifference < 0 || Math.abs(daysDifference) > thresholdDays;
  const alertRequired = daysDifference < 0; // Critical: scheduled before anticipated arrival

  return { hasMismatch, daysDifference, alertRequired };
}

/**
 * Format date for display with optional status indicator
 */
export function formatTrackingDate(
  date?: string,
  status?: DateTrackingStatus
): string {
  if (!date) return 'Not set';

  const dateObj = new Date(date);
  const formatted = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (status) {
    const statusLabels: Record<DateTrackingStatus, string> = {
      pending: '(Pending)',
      scheduled: '(Scheduled)',
      received: '(Received)',
      installed: '(Installed)',
    };
    return `${formatted} ${statusLabels[status] || ''}`;
  }

  return formatted;
}

export interface IDrawingObject {
  type: 'rect' | 'circle' | 'path' | 'line' | 'text';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  path?: string;
  text?: string;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
}

// NEW: Pipe validation status for color coding
export interface IPipeValidation {
  pipeId: string;
  status: 'valid' | 'warning' | 'error';
  color: 'blue' | 'yellow' | 'red';
  message: string;
  clearanceInches: number;
}

/**
 * Pipe collision status for visual feedback.
 * Per Clay Prewitt (2026-01-22):
 * - RED: Holes too close, touching, or overlapping
 * - BLUE: Acceptable spacing, no collision
 */
export type PipeCollisionStatus = 'collision' | 'acceptable';

export interface IPipeCollisionResult {
  pipeId: string;
  status: PipeCollisionStatus;
  collidingWithPipeIds: string[];
  minimumClearanceInches: number;
  message: string;
}

/**
 * Calculate the minimum angular separation needed between two pipe holes
 * to avoid collision on a circular manhole base.
 *
 * @param hole1DiameterInches - First hole diameter
 * @param hole2DiameterInches - Second hole diameter
 * @param structureDiameterInches - Manhole inside diameter
 * @param minimumClearanceInches - Minimum concrete between holes (default 2")
 * @returns Minimum angle in degrees
 */
export function calculateMinimumPipeAngle(
  hole1DiameterInches: number,
  hole2DiameterInches: number,
  structureDiameterInches: number,
  minimumClearanceInches: number = 2
): number {
  // Calculate arc lengths and convert to angles
  // Arc length = radius * angle (in radians)
  // Combined arc = (hole1 + hole2 + clearance * 2) / 2 for each side
  const radius = structureDiameterInches / 2;
  const combinedRadius = (hole1DiameterInches + hole2DiameterInches) / 2 + minimumClearanceInches;

  // Convert arc to angle: angle = arcLength / radius (radians)
  // Then convert to degrees
  const angleRadians = combinedRadius / radius;
  const angleDegrees = (angleRadians * 180) / Math.PI;

  return Math.max(angleDegrees, 15); // Minimum 15 degrees for practical purposes
}

/**
 * Calculate effective angular distance between two angles (0-180).
 * Accounts for the circular nature of angles.
 */
export function getAngularDistance(angle1: number, angle2: number): number {
  const diff = Math.abs(angle1 - angle2);
  return Math.min(diff, 360 - diff);
}

/**
 * Check collision between two pipes at similar elevations.
 *
 * @param pipe1 - First pipe configuration
 * @param pipe2 - Second pipe configuration
 * @param structureDiameterInches - Manhole inside diameter
 * @param elevationToleranceInches - Max elevation difference to check (default 12")
 * @returns Collision result
 */
export function checkPipePairCollision(
  pipe1: { id?: string; angleDegrees: number; holeSizeInches?: number; invertElevation: number },
  pipe2: { id?: string; angleDegrees: number; holeSizeInches?: number; invertElevation: number },
  structureDiameterInches: number,
  elevationToleranceInches: number = 12
): { hasCollision: boolean; clearanceAngle: number; minimumAngle: number } {
  // Check if pipes are at similar elevations (holes could overlap)
  const elevationDiffInches = Math.abs(pipe1.invertElevation - pipe2.invertElevation) * 12;
  if (elevationDiffInches > elevationToleranceInches) {
    // Pipes at very different elevations - no collision possible
    return { hasCollision: false, clearanceAngle: 360, minimumAngle: 0 };
  }

  const hole1 = pipe1.holeSizeInches || 12;
  const hole2 = pipe2.holeSizeInches || 12;

  const minimumAngle = calculateMinimumPipeAngle(hole1, hole2, structureDiameterInches);
  const actualAngle = getAngularDistance(pipe1.angleDegrees, pipe2.angleDegrees);

  return {
    hasCollision: actualAngle < minimumAngle,
    clearanceAngle: actualAngle - minimumAngle,
    minimumAngle,
  };
}

/**
 * Check all pipes for collisions.
 *
 * @param pipes - Array of pipe configurations
 * @param structureDiameterInches - Manhole inside diameter
 * @returns Map of pipe ID to collision results
 */
export function checkAllPipeCollisions(
  pipes: Array<{ id?: string; angleDegrees: number; holeSizeInches?: number; invertElevation: number }>,
  structureDiameterInches: number
): Map<string, IPipeCollisionResult> {
  const results = new Map<string, IPipeCollisionResult>();

  // Initialize all pipes as acceptable
  pipes.forEach((pipe, index) => {
    const pipeId = pipe.id || `pipe-${index}`;
    results.set(pipeId, {
      pipeId,
      status: 'acceptable',
      collidingWithPipeIds: [],
      minimumClearanceInches: Infinity,
      message: 'No collision detected',
    });
  });

  // Check each pair of pipes
  for (let i = 0; i < pipes.length; i++) {
    for (let j = i + 1; j < pipes.length; j++) {
      const pipe1 = pipes[i];
      const pipe2 = pipes[j];
      const pipe1Id = pipe1.id || `pipe-${i}`;
      const pipe2Id = pipe2.id || `pipe-${j}`;

      const collision = checkPipePairCollision(pipe1, pipe2, structureDiameterInches);

      if (collision.hasCollision) {
        // Update both pipes as having collision
        const result1 = results.get(pipe1Id)!;
        result1.status = 'collision';
        result1.collidingWithPipeIds.push(pipe2Id);
        result1.minimumClearanceInches = Math.min(
          result1.minimumClearanceInches,
          collision.clearanceAngle
        );
        result1.message = `Hole too close to pipe #${j + 1}`;

        const result2 = results.get(pipe2Id)!;
        result2.status = 'collision';
        result2.collidingWithPipeIds.push(pipe1Id);
        result2.minimumClearanceInches = Math.min(
          result2.minimumClearanceInches,
          collision.clearanceAngle
        );
        result2.message = `Hole too close to pipe #${i + 1}`;
      }
    }
  }

  return results;
}

// NEW: Casting options for regional compliance
export interface IManholeCasting {
  id: string;
  name: string;
  description: string;
  heightInches: number;
  type: '4inch' | '6inch' | 'wa-state' | 'yakima' | 'oregon' | 'custom';
  compatibleTypes: Array<'Storm' | 'Sanitary' | 'Custom'>;
  productCode?: string;
  weight?: number;
  cost?: number;
}

// NEW: Form assignment for production
export interface IFormAssignment {
  formId: string;
  formName: string;
  capacity: number;
  currentLoad: number;
  availableSlots: number;
  isDefault: boolean;
  alternativeForms?: string[];
}

// NEW: Balancing suggestion for optimal height
export interface IBalancingSuggestion {
  status: 'perfect' | 'need-more' | 'too-tall' | 'too-short';
  message: string;
  remainingHeight?: number;
  suggestedComponents?: ICatalogProduct[];
  suggestedAction?: string;
}

// NEW: Manhole sequence entry for multi-manhole projects
export interface IManholeSequenceEntry {
  manholeId: string;
  sequenceOrder: number;
  structureId: string;
  status: 'draft' | 'complete';
  completedAt?: Date;
}

// NEW: Manhole sequence - groups multiple manholes for a project
export interface IManholeSequence {
  id?: string;
  projectId: string;
  accountId?: number;
  jobId?: number;
  sequenceName?: string;
  description?: string;
  manholes: IManholeSequenceEntry[];
  currentIndex: number;
  totalManholes: number;
  status: 'draft' | 'in_progress' | 'complete';
  createdAt?: Date;
  updatedAt?: Date;
}

// NEW: Sealant configuration for joints
export interface ISealantConfig {
  type: 'none' | 'mastic' | 'gasket';
  gasketSize?: number; // inches: 8, 12, 15, 18, 24, 36, 48
}

// NEW: Extended internals with new options
export interface IManholeInternalsExtended extends IManholeInternals {
  hasPreChannelInvert?: boolean;
  hasGULiner?: boolean;
  hasCustomInternal?: boolean;
  customInternalDescription?: string;
  preChannelDirection?: number; // degrees, for visualization
}

/**
 * Component type values - supports both new UPPERCASE and legacy lowercase.
 * Use normalizeComponentType() for case-insensitive comparisons.
 */
export type ComponentTypeValue =
  | 'BASE' | 'RISER' | 'SECTION' | 'CONE' | 'FLAT_TOP' | 'ADJUST_RING' | 'OTHER'
  | 'base' | 'riser' | 'cone' | 'slab' | 'ring' | 'frame' | 'cover' | 'accessory';

/**
 * Normalizes a component type to UPPERCASE for consistent comparisons.
 * Handles the migration from lowercase to UPPERCASE values.
 *
 * @example
 * normalizeComponentType('base') // returns 'BASE'
 * normalizeComponentType('BASE') // returns 'BASE'
 * normalizeComponentType('slab') // returns 'SECTION' (maps legacy value)
 */
export function normalizeComponentType(type: string | undefined): string {
  if (!type) return '';
  const upper = type.toUpperCase();

  // Map legacy values to new schema
  const legacyMapping: Record<string, string> = {
    'SLAB': 'SECTION',
    'RING': 'ADJUST_RING',
    'FRAME': 'OTHER',
    'COVER': 'OTHER',
    'ACCESSORY': 'OTHER',
  };

  return legacyMapping[upper] || upper;
}

/**
 * Checks if a component type matches a target type (case-insensitive).
 * Useful for filtering and comparisons during the migration period.
 *
 * @example
 * isComponentType(product.type, 'BASE') // true for 'base', 'Base', 'BASE'
 * isComponentType(product.type, 'CONE') // true for 'cone', 'CONE'
 */
export function isComponentType(type: string | undefined, target: string): boolean {
  if (!type) return false;
  return type.toUpperCase() === target.toUpperCase() ||
         normalizeComponentType(type) === target.toUpperCase();
}

/**
 * MH-001: Component stack ordering constants and utilities.
 * Proper manhole stack order from BOTTOM to TOP:
 * 1. BASE (foundation section)
 * 2. RISERS (height adjustment sections - can be multiple)
 * 3. CONE or FLAT_TOP (transition section)
 * 4. FRAME (casting frame)
 * 5. COVER (lid/grate)
 */
export const COMPONENT_STACK_ORDER: Record<string, number> = {
  'BASE': 0,
  'RISER': 1,
  'CONE': 2,
  'FLAT_TOP': 2,  // Same order as CONE (only one or the other is used)
  'FRAME': 3,
  'COVER': 4,
};

/**
 * Get the stack order priority for a component type.
 * Lower numbers should be placed first (at bottom of stack).
 *
 * @param type - The component type string
 * @returns Order priority (0-4), or 1 (RISER level) if unknown
 */
export function getComponentStackOrder(type: string | undefined): number {
  if (!type) return 1; // Default to RISER level for unknown types
  const normalized = normalizeComponentType(type);
  return COMPONENT_STACK_ORDER[normalized] ?? 1;
}

/**
 * Check if components are in valid stacking order.
 * Returns validation result with any issues found.
 *
 * @param components - Array of components with product.type
 * @returns Validation result with isValid flag and issues array
 */
export function validateStackOrder(components: Array<{ product: { type?: string } }>): {
  isValid: boolean;
  issues: string[];
} {
  const issues: string[] = [];

  if (components.length === 0) {
    return { isValid: true, issues: [] };
  }

  // Check that each component's order priority is >= previous
  for (let i = 1; i < components.length; i++) {
    const prevOrder = getComponentStackOrder(components[i - 1].product.type);
    const currOrder = getComponentStackOrder(components[i].product.type);

    // Risers can be stacked on top of risers
    if (currOrder < prevOrder && currOrder !== prevOrder) {
      const prevType = normalizeComponentType(components[i - 1].product.type || 'UNKNOWN');
      const currType = normalizeComponentType(components[i].product.type || 'UNKNOWN');
      issues.push(`${currType} at position ${i} should be below ${prevType} at position ${i - 1}`);
    }
  }

  // Check for required BASE at bottom
  if (!isComponentType(components[0].product.type, 'BASE')) {
    issues.push('Stack should start with a BASE component');
  }

  return {
    isValid: issues.length === 0,
    issues
  };
}

/**
 * Sort components into correct stacking order.
 * Returns a new array with components sorted from bottom to top.
 *
 * @param components - Array of components with product.type
 * @returns New array with components in correct order
 */
export function sortComponentsByStackOrder<T extends { product: { type?: string } }>(
  components: T[]
): T[] {
  return [...components].sort((a, b) => {
    const orderA = getComponentStackOrder(a.product.type);
    const orderB = getComponentStackOrder(b.product.type);
    return orderA - orderB;
  });
}

/**
 * Gets the cost from a catalog product, handling both old and new field names.
 * Use this to safely get cost during the migration period.
 *
 * @example
 * const cost = getProductCost(product) // returns estimatedCost || costUSD || basePrice || 0
 */
export function getProductCost(product: ICatalogProduct | undefined): number {
  if (!product) return 0;
  return product.estimatedCost ?? product.costUSD ?? product.basePrice ?? 0;
}

/**
 * Gets the product code from a catalog product, handling both old and new field names.
 * Use this to safely get the code during the migration period.
 *
 * @example
 * const code = getProductCode(product) // returns componentCode || productId || ''
 */
export function getProductCode(product: ICatalogProduct | undefined): string {
  if (!product) return '';
  return product.componentCode || product.productId || '';
}

/**
 * Gets the invert-to-first-joint value for a base product.
 * This is the CRITICAL value for stack calculations.
 *
 * Per Clay Prewitt / NTX PDF:
 *   invertToFirstJoint = (heightInches + jointHeight - floorThickness - annularSpace) / 12
 *
 * Uses product.heightInches (catalog nominal) so height modifiers on the
 * stack component are handled separately in the caller.
 *
 * @param product - The base product
 * @returns Invert to first joint in feet, or 0 if not a base
 */
export function isMonobase(product: ICatalogProduct | undefined): boolean {
  if (!product) return false;
  const name = (product.name || '').toLowerCase();
  const desc = (product.description || '').toLowerCase();
  const code = (product.componentCode || '').toLowerCase();
  return name.includes('monobase') || desc.includes('monobase') || code.includes('monobase');
}

export function getBaseInvertToFirstJointFromProduct(
  product: ICatalogProduct | undefined,
  annularSpaceInches: number = 2
): number {
  if (!product) return 0;

  // Check if component is a base
  const type = normalizeComponentType(product.type);
  if (type !== 'BASE') return 0;

  // Use explicit value if available
  if (product.invertToFirstJointFeet !== undefined) {
    return product.invertToFirstJointFeet;
  }

  const diameterInches = product.diameterInches || 48;
  const heightInches = product.heightInches || (product.nominalHeightFeet || 3) * 12;
  const jointHeight = getJointHeightByDiameter(diameterInches);
  // Monobase is monolithically poured — floor is integral, so skip floor subtraction
  const floorThickness = isMonobase(product) ? 0 : getDefaultFloorThickness(diameterInches);

  return (heightInches + jointHeight - floorThickness - annularSpaceInches) / 12;
}

// ============================================
// Extended Manhole Pipe Type
// ============================================

/**
 * Extended manholePipe type with new fields pending backend schema update.
 * See docs/backend-requests/manhole-pipes-add-boot-hole-fields.md
 *
 * Once the backend adds bootType, defaultHoleSizeInches, and holeMethod to the
 * manhole-pipes schema and types are regenerated, this extension can be removed.
 */
export interface ManholePipeExtended {
  bootType?: string;
  defaultHoleSizeInches?: number;
  holeMethod?: string;
}